import { useCities, useCityBulkDelete, useExportCity } from "@/api/city";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { generateCustomSpec } from "@/utils/helper";
import {
  swalDelete,
  swalError,
  swalErrorDefault,
  swalSuccess,
} from "@/utils/lib/swal";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ROLE } from "@/utils/constant";
import { useCurrentUser } from "../auth/useCurrentUser";
import { DownloadButton } from "@/components/ui/button.tsx";
import { nullElement } from "@/components/null-element";

const customAdminSpec = generateCustomSpec({
  path: "/api/crud/city",
  fields: [
    {
      label: "City Name",
      name: "cityName",
      searchable: true,
      sortable: true,
    },
    {
      label: "City Code",
      name: "cityCode",
      searchable: true,
      sortable: true,
    },
    {
      label: "Province",
      name: "province.provinceName",
      searchable: true,
      sortable: true,
    },
    {
      label: "FTAX",
      name: "ftax",
      searchable: true,
      sortable: true,
    },
    {
      label: "FDT Area HPM",
      name: "fdtAreaHpm.value",
      searchable: true,
      sortable: true,
    },
    {
      label: "FIBERNODE Area HPM",
      name: "fibernodeAreaHpm.value",
      searchable: true,
      sortable: true,
    },
    {
      label: "Branch HPM",
      name: "branchHpm.value",
      searchable: true,
      sortable: true,
    },
  ],
});

const customNonAdminSpec = generateCustomSpec({
  path: "/api/crud/city",
  canBulk: false,
  fields: [
    {
      label: "City Name",
      name: "cityName",
      searchable: true,
      sortable: true,
    },
    {
      label: "City Code",
      name: "cityCode",
      searchable: true,
      sortable: true,
    },
    {
      label: "Province",
      name: "province.provinceName",
      searchable: true,
      sortable: true,
    },
    {
      label: "FTAX",
      name: "ftax",
      searchable: true,
      sortable: true,
    },
    {
      label: "FDT Area HPM",
      name: "fdtAreaHpm.value",
      searchable: true,
      sortable: true,
    },
    {
      label: "FIBERNODE Area HPM",
      name: "fibernodeAreaHpm.value",
      searchable: true,
      sortable: true,
    },
    {
      label: "Branch HPM",
      name: "branchHpm.value",
      searchable: true,
      sortable: true,
    },
  ],
});

const HeaderElement = ({ dataConfig }: { dataConfig: DataConfig }) => {
  const { mutate: exportList, isPending: isDownloading } = useExportCity();

  const handleExport = () => {
    const params = {
      ...dataConfig,
      page: undefined,
      limit: undefined,
    };

    exportList(
      {
        config: params,
      },
      {
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };
  return (
    <div className="flex items-center justify-end gap-2">
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function CityTable() {
  const [selectedRow, setSelectedRow] = useState<number[]>([]);
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const tableForm = useForm();

  const { data: cities } = useCities(dataConfig);
  const { mutate, isPending } = useCityBulkDelete();

  const { roles } = useCurrentUser();
  const isValidator = roles.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isSysAdminView = roles.includes(ROLE.SYS_ADMIN_VIEW);

  function handleBulkDelete() {
    swalDelete.fire({
      preConfirm: () => {
        mutate(
          { ids: selectedRow as unknown[] as string[] },
          {
            onSuccess: () => {
              swalSuccess.fire({
                text: "City has been deleted",
              });
            },
            onError: (error) => {
              swalError.fire({
                text: error.message,
              });
            },
            onSettled: () => {
              setSelectedRow([]);
            },
          },
        );
      },
    });
  }

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={tableForm}
        createPath={
          isValidator || isSysAdminView ? null : "/master-data/city/create"
        }
        selectedIds={selectedRow as unknown as string[]}
        handleBulkDelete={
          isValidator || isSysAdminView ? null : handleBulkDelete
        }
        isPendingDelete={isPending}
      />
      <TableDefault
        spec={isValidator ? customNonAdminSpec : customAdminSpec}
        data={cities}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        searchFilterByColumns={true}
        headerElement={<HeaderElement dataConfig={dataConfig} />}
        customButtonEdit={isSysAdminView ? nullElement : undefined}
      />
    </>
  );
}
